import React, { Fragment, useContext, useState } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import Box from '@material-ui/core/Box';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import Switch from '@material-ui/core/Switch';
import { useSelector, useDispatch } from 'react-redux';
import Backdrop from '@material-ui/core/Backdrop';
import { createSvgIcon } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import useStyles from './Modal.styled';
import { UserContext } from 'context';
import { useHistory } from 'react-router-dom';
import { UserSettingsActions } from 'state/modules/userSettings';
import { usersActions } from 'state/modules/users';
import {
  getLMKBUrl,
  buildUrl,
  userIsAllowed,
  getSideBarConfig,
  getPrivilegesList,
  getConfigurationsList,
} from 'utils';
import { SlideInDown } from './SlideInDown';
import clx from 'clsx';
import i18n from 'i18n-js';

const TriangleIcon = createSvgIcon(
  <path d="M0.604248 6.90308L4.60425 3.90308L0.604248 0.903076L0.604248 6.90308Z" fill="white" />,
  'Arrow Icon'
);

const LMKBIcon = createSvgIcon(
  <path d="m19 1-5 5v11l5-4.5V1zM1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5V6c-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6zm22 13.5V6c-.6-.45-1.25-.75-2-1v13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5v2c1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5v-1.1z" />,
  'LMKB Icon'
);

const GuidingModal = ({ maxWidth, title, openModal, id }) => {
  const core = useSelector((state) => state.config?.settings?.core || {});
  const links = useSelector(
    (state) => state.config?.settings?.integrations?.settings?.walkthrough?.links_of_interest || []
  );
  const { fetchingUser } = useSelector((state) => state?.auth);
  const { id: userid, preferences } = useSelector((state) => state.auth?.user || {});
  const { walkthrough_permanent } = preferences || { walkthrough_permanent: true };
  const { orgType } = useContext(UserContext);

  const [activeIndex, setActiveIndex] = useState(0);
  const [showOnStartup, setShowOnStartup] = useState(walkthrough_permanent);

  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const baseUrl = getLMKBUrl();
  const privileges = getPrivilegesList();
  const configurations = getConfigurationsList();
  const permissions = { privileges, configurations };

  const getConfig = (category, label) => {
    const config = getSideBarConfig({ category, label });
    return { ...config, ...permissions };
  };

  const shouldRender = (category, label) => userIsAllowed(getConfig(category, label));

  const baseUrlSettingMC = `${baseUrl}docs/Setting_up_Mission_Control`;
  const isOwner = orgType === 'msOwnerOrg';
  const isService = orgType === 'msServiceOrg';
  const hasSubscription = core?.subscription?.enabled || false;

  // const guidingBody = {
  //   ...(isOwner
  //     ? {
  //         title: i18n.t('guiding.fleets'),
  //         paragraph: i18n.t('guiding.description_fleets'),
  //         subParagraph: i18n.t('guiding.call_to_action'),
  //       }
  //     : {}),
  //   ...(isService
  //     ? {
  //         title: i18n.t('guiding.service_groups'),
  //         paragraph: i18n.t('guiding.description_service_groups'),
  //         subParagraph: i18n.t('guiding.call_to_action'),
  //       }
  //     : {}),
  //   ...(hasSubscription
  //     ? {
  //         title: i18n.t('guiding.service_groups'),
  //         paragraph: i18n.t('guiding.description_flatOrg'),
  //         subParagraph: i18n.t('guiding.call_to_action'),
  //       }
  //     : {}),
  // };

  const guidingBody = {
    intro: i18n.t('guiding.default_intro'),
    title: isOwner ? i18n.t('guiding.owner_description') : i18n.t('guiding.default_description'),
    subParagraph: i18n.t('guiding.default_call_to_action'),
  };

  const steps = [
    {
      render: shouldRender('location_management', 'locations') && isOwner,
      title: i18n.t('guiding.steps.locations'),
      tutorialLink: `${baseUrl}how-to-create-a-location`,
      createLink: `/locations`,
    },
    {
      render: shouldRender('location_management', 'fleets') && isOwner,
      title: i18n.t('guiding.steps.fleets'),
      tutorialLink: `${baseUrl}how-to-create-a-fleet`,
      createLink: `/fleets`,
    },
    {
      render: shouldRender('vehicle_management', 'vehicle_types') && isOwner,
      title: i18n.t('guiding.steps.vehicle_types'),
      tutorialLink: `${baseUrl}how-to-create-a-vehicle-type`,
      createLink: `/vehicle-types`,
    },
    {
      render: shouldRender('vehicle_management', 'vehicles') && isOwner,
      title: i18n.t('guiding.steps.vehicles'),
      tutorialLink: `${baseUrl}how-to-create-a-vehicle`,
      createLink: `/vehicles`,
    },
    {
      render: shouldRender('organization', 'users_groups') && isService,
      title: i18n.t('guiding.steps.user_group'),
      tutorialLink: `${baseUrl}how-to-create-a-user-group`,
      createLink: `/users-groups`,
    },
    {
      render: shouldRender('pricing', 'skus') && isService,
      title: i18n.t('guiding.steps.skus'),
      tutorialLink: `${baseUrlSettingMC}how-to-add-a-sku-in-mission-control`,
      createLink: `/skus`,
    },
    {
      render: shouldRender('pricing', 'pricing_rulesets') && isService,
      title: i18n.t('guiding.steps.pricing_rulesets'),
      tutorialLink: `${baseUrlSettingMC}create-a-pricing-ruleset`,
      createLink: `/pricing-rulesets`,
    },
    {
      render: shouldRender('location_management', 'business_hours') && isService,
      title: i18n.t('guiding.steps.business_hours'),
      tutorialLink: `${baseUrl}how-to-create-and-assign-a-business-hours-ruleset`,
      createLink: `/business-hours`,
    },
    {
      render: shouldRender('location_management', 'vehicles_groups') && isService,
      title: i18n.t('guiding.steps.service_groups'),
      tutorialLink: `${baseUrl}how-to-create-a-service-group`,
      createLink: `/service-groups`,
    },
    {
      render: shouldRender('location_management', 'locations') && hasSubscription,
      title: i18n.t('guiding.steps.locations'),
      tutorialLink: `${baseUrl}how-to-create-a-location`,
      createLink: `/locations`,
    },
    {
      render: shouldRender('vehicle_management', 'vehicle_types') && hasSubscription,
      title: i18n.t('guiding.steps.vehicle_types'),
      tutorialLink: `${baseUrl}how-to-create-a-vehicle-type`,
      createLink: `/vehicle-types`,
    },
    {
      render: shouldRender('vehicle_management', 'vehicles') && hasSubscription,
      title: i18n.t('guiding.steps.vehicles'),
      tutorialLink: `${baseUrl}how-to-create-a-vehicle`,
      createLink: `/vehicles`,
    },
    {
      render: shouldRender('organization', 'users_groups') && hasSubscription,
      title: i18n.t('guiding.steps.user_group'),
      tutorialLink: `${baseUrl}how-to-create-a-user-group`,
      createLink: `/users-groups`,
    },
    {
      render: shouldRender('pricing', 'skus') && hasSubscription,
      title: i18n.t('guiding.steps.skus'),
      tutorialLink: `${baseUrlSettingMC}how-to-add-a-sku-in-mission-control`,
      createLink: `/skus`,
    },
    {
      render: shouldRender('pricing', 'pricing_rulesets') && hasSubscription,
      title: i18n.t('guiding.steps.pricing_rulesets'),
      tutorialLink: `${baseUrlSettingMC}create-a-pricing-ruleset`,
      createLink: `/pricing-rulesets`,
    },
    {
      render: shouldRender('location_management', 'business_hours') && hasSubscription,
      title: i18n.t('guiding.steps.business_hours'),
      tutorialLink: `${baseUrl}how-to-create-and-assign-a-business-hours-ruleset`,
      createLink: `/business-hours`,
    },
    {
      render: shouldRender('location_management', 'vehicles_groups') && hasSubscription,
      title: i18n.t('guiding.steps.service_groups'),
      tutorialLink: `${baseUrl}how-to-create-a-service-group`,
      createLink: `/service-groups`,
    },
  ];

  const handleChange = (event) => {
    const payload = {
      preferences: {
        walkthrough_permanent: event.target.checked,
      },
    };
    dispatch(usersActions.fetchUpdateUser(userid, payload));
    setShowOnStartup(event.target.checked);
  };

  const handleClose = () => {
    openModal();
    dispatch(UserSettingsActions.handleShowGuidingModal(false));
  };

  const filteredSteps = steps.filter((step) => step.render);
  const filteredLinks = links.filter((link) => link.link);
  const lenSteps = filteredSteps.length || 0;
  const limitDisplaySteps = 6;

  const renderSteps = () => {
    return filteredSteps.map((step, index) => (
      <Fragment key={`step-${index + 1}`}>
        <Box className={classes.bodyStep}>
          <Box className={classes.stepTop}>
            <span className={classes.stepNumber}>{index + 1}</span>
            <div className={classes.circle} />
          </Box>
          <h2 className={classes.stepTitle}>{step.title}</h2>
          <Box className={classes.stepBottom}>
            <Button
              target="_blank"
              onClick={() => {
                openModal();
                dispatch(UserSettingsActions.handleShowGuidingModal(false));
                history.push(buildUrl(step.createLink));
              }}
              className={classes.actionButton}
            >
              <div>{i18n.t('guiding.create')}</div>
              <div>
                <TriangleIcon viewBox="0 0 6 7" className={classes.rectangle} />
              </div>
            </Button>
            <Button
              target="_blank"
              href={`${step.tutorialLink}`}
              className={classes.tutorialButton}
            >
              <div>{i18n.t('guiding.tutorial')}</div>
              <div>
                <TriangleIcon viewBox="0 0 6 7" className={classes.rectangle} />
              </div>
            </Button>
          </Box>
        </Box>
        {lenSteps !== index + 1 ? <div className={classes.lineVertical} /> : null}
      </Fragment>
    ));
  };

  return (
    <Modal
      open={openModal}
      id={id || 'guiding modal'}
      onClose={openModal}
      className={classes.modal}
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      aria-labelledby={`modal-${title}`}
      aria-describedby={`modal-${title}`}
    >
      <SlideInDown in={true}>
        <Box className={clsx(classes.modalContainer)} style={{ maxWidth }}>
          <Box className={classes.modalHeader}>
            <a href={baseUrl} target="_blank" rel="noreferrer">
              <Box className={classes.headerTitle}>
                <LMKBIcon className={classes.notificationsIcon} />

                <h2 data-cy="modalTitle" className={classes.headerTitle}>
                  {title || i18n.t('guiding.title_modal')}
                </h2>
              </Box>
            </a>
            <Box className={classes.headerButtons}>
              <Switch
                onChange={handleChange}
                checked={showOnStartup}
                name="show-on-startup"
                disabled={fetchingUser}
                classes={{
                  switchBase: classes.switchBase,
                  thumb: showOnStartup ? classes.thumbOn : classes.thumbOff,
                  track: classes.track,
                }}
              />
              <span style={{ color: '#FFFFFF' }}>{i18n.t('guiding.show_on_startup')}</span>
              <span className={classes.verticalToogleLine} />
              <ClearIcon onClick={() => handleClose()} className={classes.exitIcon} />
            </Box>
          </Box>
          <Box className={classes.modalBody}>
            <Box className={classes.bodyBoxTitles}>
              <span className={classes.bodyTitle}>
                {guidingBody.intro ? guidingBody.intro : i18n.t('guiding.intro')}
              </span>
              <span className={classes.bodySubTitle}>{guidingBody.title}</span>
            </Box>
            <Box className={classes.bodyBoxParagraphs}>
              <p className={classes.bodyParagraph}>
                {guidingBody.paragraph ? <span>{guidingBody.paragraph}</span> : null}
                {guidingBody.subParagraph ? <span>{guidingBody.subParagraph}</span> : null}
              </p>
            </Box>
            {lenSteps <= limitDisplaySteps ? (
              <div className={classes.bodyBoxSteps2}>{renderSteps()}</div>
            ) : (
              <Box className={classes.carousel}>
                <button
                  onClick={() => setActiveIndex(activeIndex > 0 ? activeIndex - 1 : 0)}
                  className={clx(classes.arrowButton, classes.mirror)}
                >
                  <TriangleIcon
                    viewBox="0 0 4 7"
                    style={{
                      width: '19px',
                      height: '18px',
                    }}
                  />
                </button>
                <Box className={classes.bodyBoxSteps}>
                  <div
                    className={classes.carouselBox}
                    style={{
                      transform: `translateX(-${144 * activeIndex}px)`,
                    }}
                  >
                    {renderSteps()}
                  </div>
                  <Box
                    className={classes.boxGradient}
                    style={{
                      display:
                        lenSteps >= limitDisplaySteps &&
                        activeIndex !== lenSteps - limitDisplaySteps
                          ? null
                          : 'none',
                    }}
                  />
                </Box>
                <button
                  onClick={() => {
                    setActiveIndex(
                      activeIndex < lenSteps - limitDisplaySteps ? activeIndex + 1 : activeIndex
                    );
                  }}
                  className={classes.arrowButton}
                >
                  <TriangleIcon
                    viewBox="0 0 4 7"
                    style={{
                      width: '19px',
                      height: '18px',
                    }}
                  />
                </button>
              </Box>
            )}
            {filteredLinks.length > 0 ? (
              <Box className={classes.moreInfo}>
                <div className={classes.lineHorizontal} />
                <Box className={classes.moreInfoParagraphs}>
                  <h2 className={classes.titleMoreInfo}>{i18n.t('guiding.more_title')}</h2>
                  {filteredLinks.map((link, index) => (
                    <a
                      key={`link-${index}`}
                      href={link.link}
                      target="_blank"
                      className={classes.link}
                      rel="noreferrer"
                    >
                      {link.text || link.link}
                    </a>
                  ))}
                </Box>
              </Box>
            ) : null}
          </Box>
          <Box className={classes.modalFooter}>
            <Button href={baseUrl} target="_blank" className={classes.footerButton}>
              {i18n.t('guiding.see_all_tutorials')}
            </Button>
          </Box>
        </Box>
      </SlideInDown>
    </Modal>
  );
};

GuidingModal.propTypes = {
  title: PropTypes.string,
  maxWidth: PropTypes.string,
  openModal: PropTypes.func,
  id: PropTypes.string,
};

GuidingModal.defaultProps = {
  title: '',
  maxWidth: '',
  openModal: () => {},
  id: '',
};

export { GuidingModal };
